import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import SidebarSubmenu from './SidebarSubmenu';

interface SubItem {
  name: string;
  href: string;
  description: string;
}

interface SidebarItemProps {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  activeIcon?: React.ComponentType<any>;
  description?: string;
  badge?: string;
  badgeColor?: string;
  subItems?: SubItem[];
  isCollapsed: boolean;
  isActive: boolean;
  onNavigate?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  name,
  href,
  icon: Icon,
  activeIcon: ActiveIcon,
  description,
  badge,
  badgeColor,
  subItems,
  isCollapsed,
  isActive,
  onNavigate,
}) => {
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();

  const DisplayIcon = isActive && ActiveIcon ? ActiveIcon : Icon;
  const hasSubItems = subItems && subItems.length > 0;
  
  // Auto-expand if any subitem is active
  useEffect(() => {
    if (hasSubItems && !isCollapsed) {
      const hasActiveSub = subItems?.some(subItem => isSubItemActive(subItem.href)) || false;
      if (hasActiveSub) {
        setIsExpanded(true);
      }
    }
  }, [location.pathname, hasSubItems, isCollapsed, subItems]);

  const isSubItemActive = (subHref: string) => {
    // Exact match
    if (location.pathname === subHref) {
      return true;
    }
    // Check if it's a child path (starts with subHref + '/')
    // But exclude cases where subHref is a parent of another menu item
    if (location.pathname.startsWith(subHref + '/')) {
      // Special handling: if subHref is /staff, don't match /staff/new
      if (subHref === '/staff' && location.pathname.startsWith('/staff/new')) {
        return false;
      }
      return true;
    }
    return false;
  };

  // Check if any subitem is active
  const hasActiveSubItem = subItems?.some(subItem => isSubItemActive(subItem.href)) || false;

  // Parent should be active if itself is active OR any subitem is active
  const isParentActive = isActive || hasActiveSubItem;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!isCollapsed || !hasSubItems) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setSubmenuPosition({
      x: rect.right,
      y: rect.top,
    });
    setShowSubmenu(true);
  };

  const handleMouseLeave = () => {
    if (!isCollapsed) return;

    timeoutRef.current = setTimeout(() => {
      setShowSubmenu(false);
    }, 300);
  };

  const handleSubmenuEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowSubmenu(true);
  };

  const handleSubmenuLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowSubmenu(false);
    }, 300);
  };

  const handleClick = () => {
    if (hasSubItems) {
      if (isCollapsed) {
        setShowSubmenu(!showSubmenu);
      } else {
        // Toggle expand/collapse when not collapsed
        setIsExpanded(!isExpanded);
      }
    }
  };

  if (href === '#' && hasSubItems) {
    // Parent item with submenus - no link
    return (
      <div
        ref={itemRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="relative"
      >
        <div
          onClick={handleClick}
          className={`group relative flex items-center px-2 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 cursor-pointer ${
            isParentActive
              ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
              : 'text-slate-600 hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-300 hover:text-gray-800 border border-slate-200/50 hover:border-gray-300/50'
          } ${isCollapsed ? 'justify-center' : ''}`}
        >
          {isParentActive && !isCollapsed && (
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-gray-400 rounded-r-full" />
          )}

          <div
            className={`${isCollapsed ? '' : 'mr-3'} h-7 w-7 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-gray-200 group-hover:to-gray-300 shadow-sm group-hover:shadow-md`}
          >
            <DisplayIcon
              className={`h-3.5 w-3.5 transition-colors duration-300 ${
                isParentActive ? 'text-gray-700' : 'text-slate-500 group-hover:text-gray-700'
              }`}
            />
          </div>

          {!isCollapsed && (
            <div className="flex-1 min-w-0 flex items-center">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <span className="font-medium text-sm break-words leading-tight text-left">{name}</span>
                  {badge && (
                    <span
                      className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0 ${
                        badgeColor || 'bg-red-500'
                      } animate-pulse`}
                    >
                      {badge}
                    </span>
                  )}
                </div>
              </div>
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 ml-auto flex-shrink-0" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 ml-auto flex-shrink-0" />
              )}
            </div>
          )}
        </div>

        {/* Expanded submenu */}
        {!isCollapsed && hasSubItems && isExpanded && (
          <div className="ml-6 mt-1 space-y-0.5 animate-expandDown">
            {subItems.map((subItem) => {
              const isSubActive = isSubItemActive(subItem.href);
              return (
                <NavLink
                  key={subItem.name}
                  to={subItem.href}
                  className={`group relative flex items-center px-2 py-0.5 text-xs font-medium rounded-lg transition-all duration-300 ${
                    isSubActive
                      ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
                      : 'text-slate-500 hover:bg-gradient-to-r hover:from-slate-100 hover:to-gray-200 hover:text-gray-700 border border-slate-200/30 hover:border-gray-300/50'
                  }`}
                >
                  {isSubActive && (
                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-gray-400 rounded-r-full" />
                  )}

                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-xs leading-tight truncate block">{subItem.name}</span>
                  </div>
                </NavLink>
              );
            })}
          </div>
        )}

        {/* Collapsed popover submenu */}
        {isCollapsed && showSubmenu && hasSubItems && (
          <SidebarSubmenu
            items={subItems}
            position={submenuPosition}
            onMouseEnter={handleSubmenuEnter}
            onMouseLeave={handleSubmenuLeave}
            title={name}
          />
        )}
      </div>
    );
  }

  // Regular navigation item (with actual href, not '#')
  return (
    <div className="relative">
      <NavLink
        to={href}
        onClick={() => {
          if (onNavigate) {
            onNavigate();
          }
        }}
        className={`group relative flex items-center px-2 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 ${
          isActive || (hasSubItems && hasActiveSubItem)
            ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
            : 'text-slate-600 hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-300 hover:text-gray-800 border border-slate-200/50 hover:border-gray-300/50'
        } ${isCollapsed ? 'justify-center' : ''}`}
      >
        {isActive && !isCollapsed && (
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-gray-400 rounded-r-full" />
        )}

        <div
          className={`${isCollapsed ? '' : 'mr-3'} h-7 w-7 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-gray-200 group-hover:to-gray-300 shadow-sm group-hover:shadow-md`}
        >
          <DisplayIcon
            className={`h-3.5 w-3.5 transition-colors duration-300 ${
              isActive || (hasSubItems && hasActiveSubItem)
                ? 'text-gray-700'
                : 'text-slate-500 group-hover:text-gray-700'
            }`}
          />
        </div>

        {!isCollapsed && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <span className="font-medium text-sm break-words leading-tight text-left">{name}</span>
              {badge && (
                <span
                  className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold text-white flex-shrink-0 ${
                    badgeColor || 'bg-red-500'
                  } animate-pulse`}
                >
                  {badge}
                </span>
              )}
            </div>
          </div>
        )}
      </NavLink>

      {/* Expanded submenu for regular items */}
      {!isCollapsed && hasSubItems && isExpanded && (
        <div className="ml-6 mt-1 space-y-0.5">
          {subItems.map((subItem) => {
            const isSubActive = isSubItemActive(subItem.href);
            return (
              <NavLink
                key={subItem.name}
                to={subItem.href}
                onClick={() => {
                  if (onNavigate) {
                    onNavigate();
                  }
                }}
                className={`group relative flex items-center px-2 py-0.5 text-xs font-medium rounded-lg transition-all duration-300 ${
                  isSubActive
                    ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 shadow-md border border-gray-300'
                    : 'text-slate-500 hover:bg-gradient-to-r hover:from-slate-100 hover:to-gray-200 hover:text-gray-700 border border-slate-200/30 hover:border-gray-300/50'
                }`}
              >
                {isSubActive && (
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-gray-400 rounded-r-full" />
                )}

                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-xs leading-tight truncate block">{subItem.name}</span>
                </div>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarItem;
